import React, { useEffect, useState } from "react";
import { Download, CheckCircle, AlertTriangle, CreditCard } from "lucide-react";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

function fmt(n) { return n != null ? `₹${Number(n).toLocaleString("en-IN")}` : "—"; }

export default function BankDisbursementTab({ month, runStatus }) {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [busy,     setBusy]     = useState(null);
  const [msg,      setMsg]      = useState(null);
  const [failed,   setFailed]   = useState([]);
  const token = localStorage.getItem("token");

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/admin/payroll/bank/summary?month=${month}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API}/api/admin/payroll/bank/failed?month=${month}`,  { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    ])
      .then(([d, f]) => { setData(d); setFailed(Array.isArray(f) ? f : []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [month]);

  const downloadBankFile = async (format) => {
    setBusy("download"); setMsg(null);
    try {
      const res = await fetch(`${API}/api/admin/payroll/export/bank-file?month=${month}&format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const ext  = format === "neft" ? "txt" : "csv";
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `bank_transfer_${month}.${ext}`; a.click();
      URL.revokeObjectURL(url);
      setMsg({ type: "success", text: `Bank transfer file downloaded.` });
    } catch (e) { setMsg({ type: "error", text: e.message }); }
    finally { setBusy(null); }
  };

  const markDisbursed = async () => {
    setBusy("disburse"); setMsg(null);
    try {
      const res = await fetch(`${API}/api/admin/payroll/run/disburse?month=${month}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMsg({ type: "success", text: "Run marked as disbursed." });
    } catch (e) { setMsg({ type: "error", text: e.message }); }
    finally { setBusy(null); }
  };

  const retryFailed = async (id) => {
    try {
      await fetch(`${API}/api/admin/payroll/bank/retry/${id}?month=${month}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      setFailed(f => f.filter(x => x.id !== id));
      setMsg({ type: "success", text: "Payment queued for retry." });
    } catch (e) { setMsg({ type: "error", text: e.message }); }
  };

  return (
    <div className="space-y-5">

      {/* Summary */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <CreditCard size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Bank Disbursement — {month}</span>
        </div>
        <div className="p-5 space-y-4">
          {loading ? <div className="h-20 rounded-lg bg-white/5 animate-pulse" /> : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Total Transfers",    value: data?.total_count     ?? "—",        color: "#00ff90"  },
                { label: "Total Amount",       value: fmt(data?.total_amount),              color: "#fbbf24"  },
                { label: "Disbursed",          value: data?.disbursed_count ?? "—",        color: "#60a5fa"  },
                { label: "Failed",             value: failed.length,                        color: failed.length > 0 ? "#f87171" : "rgba(255,255,255,0.3)" },
              ].map(({ label, value, color }) => (
                <div key={label} className="rounded-lg border border-white/5 bg-white/3 p-3 text-center">
                  <p className="text-xl font-bold" style={{ color }}>{value}</p>
                  <p className="text-xs text-white/30 mt-0.5">{label}</p>
                </div>
              ))}
            </div>
          )}

          {msg && (
            <div className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-sm ${
              msg.type === "error" ? "border-red-500/30 bg-red-500/10 text-red-300" : "border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90]"
            }`}>
              {msg.type === "error" ? <AlertTriangle size={14} /> : <CheckCircle size={14} />}
              {msg.text}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <button onClick={() => downloadBankFile("neft")} disabled={busy !== null}
              className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] disabled:opacity-40 transition-colors">
              <Download size={12} /> NEFT File
            </button>
            <button onClick={() => downloadBankFile("ach")} disabled={busy !== null}
              className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border border-blue-500/25 bg-blue-500/8 text-blue-400/70 hover:bg-blue-500/15 hover:text-blue-400 disabled:opacity-40 transition-colors">
              <Download size={12} /> ACH File
            </button>
            <button onClick={() => downloadBankFile("csv")} disabled={busy !== null}
              className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border border-white/10 bg-white/5 text-white/40 hover:text-white disabled:opacity-40 transition-colors">
              <Download size={12} /> CSV
            </button>
            {runStatus === "LOCKED" && (
              <button onClick={markDisbursed} disabled={busy !== null}
                className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border border-purple-500/30 bg-purple-500/10 text-purple-400/80 hover:bg-purple-500/20 hover:text-purple-400 disabled:opacity-40 transition-colors ml-auto">
                <CheckCircle size={12} /> Mark as Disbursed
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Failed payments */}
      {failed.length > 0 && (
        <div className={card}>
          <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle size={13} className="text-red-400" />
              <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Failed / Returned Payments</span>
            </div>
            <span className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded-full">{failed.length}</span>
          </div>
          <div className="divide-y divide-white/5">
            {failed.map(f => (
              <div key={f.id} className="flex items-center justify-between gap-3 px-5 py-3">
                <div className="min-w-0">
                  <p className="text-sm text-white/70">{f.employee_name} <span className="text-white/30 text-xs">{f.employee_id}</span></p>
                  <p className="text-xs text-white/30 mt-0.5">{fmt(f.amount)} · {f.bank_name} ···{f.account_last4} · {f.reason}</p>
                </div>
                <button onClick={() => retryFailed(f.id)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors shrink-0">
                  Retry
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}